import { UnionSmallIcon } from "@/assets/images/svgIcons/unionSmallIcon"
import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import Vector from "@images/background/vector.svg"
import OnboardingLiteVersion from "@images/payment/onboardingLiteVersion.svg"
import { BoxOnboardingWrapper } from "@modules/box/components/boxOnboardingWrapper"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { ArrowLeftIcon } from "lucide-react"
import { useNavigate } from "react-router"
import styled from "styled-components"

/**
 * This page is used for Stripe onboarding
 * so that the owners can create their Stripe account
 * @returns {React.ReactNode} - Wrapper & Header & BoxOnboardingStripeFirstPage | BoxOnboardingStripeSecondPage
 */
export const BoxOnboardingLiteVersion: React.FC = () => {
  const navigate = useNavigate()
  const boxNavigate = useBoxNavigate()
  const popup = useConfirmPopup()
  const { setTheme } = useTheme()

  const handleClose = () =>
    popup.show({
      title: word("box.creation.form.cancel.title"),
      message: word("box.creation.form.cancel.description"),
      accept: async () => {
        setTheme(Theme.AthleteLight)
        setTimeout(() => navigate(Path.Root), 100)
      },
    })

  return (
    <BoxOnboardingWrapper backgroundImage={Vector} handleClose={handleClose}>
      <div className="relative flex w-full flex-col justify-between p-12">
        {/* HEARDER */}
        <div className="flex w-full flex-col items-center gap-2 text-center">
          <UnionSmallIcon color="currentColor" />

          <Text fontStyle="HL" className="mt-5">
            {word("box.stripe.liteVersion.title")}
          </Text>
          <Text fontStyle="BL">{word("box.stripe.liteVersion.subtitle")}</Text>
        </div>

        {/* CONTENT */}
        <div className="mx-auto flex max-w-max flex-col gap-6">
          <Text fontStyle="HM" className="mx-auto flex w-10/12 px-4 text-center sm:w-8/12">
            {word("box.stripe.liteVersion.content.title")}
          </Text>

          <div className="my-4 flex grow flex-col items-center justify-center gap-6 sm:flex-row">
            <div className="flex w-1/4 flex-col gap-4 sm:w-1/2">
              <Text fontStyle="TS">{word("box.stripe.liteVersion.content.bulletPoints.title")}</Text>

              <ul className="mt-4 flex flex-col gap-3 pl-4">
                {Array.from({ length: 5 }, (_, index) => (
                  <Li key={index}>
                    <Text fontStyle="BM" className="text-neutral-600">
                      {word(`box.stripe.liteVersion.content.bulletPoints.bullet${index + 1}` as keyof typeof word)}
                    </Text>
                  </Li>
                ))}
              </ul>

              <div className="my-4 flex flex-row justify-center gap-4">
                <Button variant="outline" size="lg" onClick={() => boxNavigate(Path.Box.OnboardingStripe)}>
                  <ArrowLeftIcon color="currentColor" />
                  {word("global.previous")}
                </Button>

                <Button variant="primary" size="lg" onClick={() => boxNavigate(Path.Root)}>
                  {word("box.stripe.liteVersion.content.button")}
                </Button>
              </div>
            </div>

            <div
              className="h-40 w-full bg-contain bg-center bg-no-repeat sm:h-88 sm:w-5/12"
              style={{ backgroundImage: `url('${OnboardingLiteVersion} ')` }}
            ></div>
          </div>
        </div>
      </div>
    </BoxOnboardingWrapper>
  )
}

const Li = styled.li`
  display: flex;
  align-items: center;
  position: relative;

  &::before {
    content: "•";
    color: var(--primary-400);
    font-size: 4em;
    position: absolute;
    left: -0.4em;
  }
`
